import { useState } from 'react';
import { toast } from 'sonner';
import { AppAlert } from '@/components/AppAlert';
import { confirmDialog } from '@/components/ConfirmDialog';
import { Button } from '@/components/ui/button';
import { useAuthStore } from '@/stores/authStore';
import { switchActiveTeam } from '@/utils/switchTeam';
import { confirmDiscardUnsavedChanges } from '@/utils/unsavedChanges';

/**
 * Another tab switched team. This tab keeps its own team (decision 8 in the T-150 audit) and
 * offers to follow. Stay here is a button rather than `onDismiss`, whose corner button would sit
 * over the action column.
 */
export function OtherTabTeamNotice({
  teamName,
  switching = false,
  onSwitch,
  onStay,
  className,
}: {
  teamName: string;
  switching?: boolean;
  onSwitch: () => void;
  onStay: () => void;
  className?: string;
}) {
  return (
    <AppAlert
      tone="info"
      role="status"
      className={className}
      action={
        <div className="flex items-center gap-2">
          <Button size="xs" variant="outline" loading={switching} onClick={onSwitch}>
            Switch
          </Button>
          <Button size="xs" variant="ghost" disabled={switching} onClick={onStay}>
            Stay here
          </Button>
        </div>
      }
    >
      You switched to {teamName} in another tab.
    </AppAlert>
  );
}

/** The notice for this tab, from the auth store (use-auth.tsx listens to the other tabs). */
export function OtherTabTeamBanner({ className }: { className?: string }) {
  const otherTabTeam = useAuthStore((s) => s.otherTabTeam);
  const setOtherTabTeam = useAuthStore((s) => s.setOtherTabTeam);
  const [switching, setSwitching] = useState(false);

  if (!otherTabTeam) return null;

  const handleSwitch = async () => {
    const discard = await confirmDiscardUnsavedChanges((message) =>
      confirmDialog({ title: 'Switch team?', description: message, confirmLabel: 'Discard and switch', destructive: true })
    );
    if (!discard) return;
    setSwitching(true);
    try {
      // Reloads on success, so `switching` only needs resetting on failure.
      await switchActiveTeam(otherTabTeam.id);
    } catch (error) {
      console.error('Error switching team:', error);
      toast.error(`Could not switch to ${otherTabTeam.name}. Try again.`);
      setSwitching(false);
    }
  };

  return (
    <OtherTabTeamNotice
      teamName={otherTabTeam.name}
      switching={switching}
      onSwitch={() => void handleSwitch()}
      onStay={() => setOtherTabTeam(null)}
      className={className}
    />
  );
}
